import React, { useEffect, useState } from 'react';

import api from '../utils/Api';
import { ReviewShoutout, ShoutoutBan, ShoutoutMode } from '../utils/Shoutouts';
import { formatPrice } from './BuyBumps';

import './ShoutoutReview.scss';

// Checked this often for new shout-outs waiting for review, while there can be any.
const REFRESH_MS = 20 * 1000;

interface ReviewState {
  pending: ReviewShoutout[];
  /** On the TV now, or waiting for their popup. */
  live: ReviewShoutout[];
  recent: ReviewShoutout[];
  bans: ShoutoutBan[];
  /** How long an approved one stays on the TV. */
  minutes: number;
}

const toState = (response: any): ReviewState => ({
  pending: (response && response.pending) || [],
  live: (response && response.live) || [],
  recent: (response && response.recent) || [],
  bans: (response && response.bans) || [],
  minutes: (response && response.minutes) || 10,
});

/** What the control panel does to a shout-out. */
export type ShoutoutAction = 'approve' | 'reject' | 'remove' | 'ban';

/**
 * The control panel's shout-outs: what waits for review, what is on the TV, and who is banned
 * from the ticker. Read while shout-outs are on, and once when they are off in case some
 * still wait or show (they stay listed so they can be dealt with either way).
 */
export const useShoutoutReview = (playerIdentifier: string, token: string, enabled: boolean, mode: ShoutoutMode) => {
  const [state, setState] = useState<ReviewState>({ pending: [], live: [], recent: [], bans: [], minutes: 10 });
  const [busyId, setBusyId] = useState<number>(null);

  const refresh = () =>
    api
      .shoutouts(`${playerIdentifier}/review`, 'GET', token, null)
      .then(response => setState(toState(response)))
      .catch(error => console.log('Could not load shout-outs: ' + error.message));

  const waiting = state.pending.length > 0 || state.live.length > 0;
  useEffect(() => {
    if (!enabled) return;
    refresh();
    if (mode === 'off' && !waiting) return;
    const timer = window.setInterval(refresh, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [enabled, mode, waiting, playerIdentifier, token]);

  const send = async (busy: number, path: string) => {
    setBusyId(busy);
    try {
      const response = await api.shoutouts(`${playerIdentifier}/${path}`, 'POST', token, {});
      setState(toState(response));
      return { problem: null as string, response };
    } catch (error) {
      refresh();
      return { problem: error.message as string, response: null };
    } finally {
      setBusyId(null);
    }
  };

  /** Answers a problem to show, or null when it worked. */
  const decide = async (shoutout: ReviewShoutout, action: ShoutoutAction) =>
    (await send(shoutout.id, `review/${shoutout.id}/${action}`)).problem;

  /** Bans the buyer of a shout-out. Answers a problem, or what the ban did. */
  const ban = (shoutout: ReviewShoutout) => send(shoutout.id, `review/${shoutout.id}/ban`);

  const unban = async (userId: number) => (await send(-userId, `bans/${userId}/unban`)).problem;

  return { ...state, busyId, refresh, decide, ban, unban };
};

const MODE_CHOICES: { mode: ShoutoutMode; label: string }[] = [
  { mode: 'off', label: 'Off' },
  { mode: 'auto', label: 'Auto-play' },
  { mode: 'review', label: 'Review first' },
];

interface ShoutoutModeSettingProps {
  mode: ShoutoutMode;
  minutes: number;
  disabled: boolean;
  onChange(mode: ShoutoutMode): void;
}

/** The owner's choice in House rules. */
export const ShoutoutModeSetting = ({ mode, minutes, disabled, onChange }: ShoutoutModeSettingProps) => (
  <div className="control-rule">
    <div className="control-rule-label" id="shoutout-mode-label">
      Paid shout-outs on the TV
    </div>
    <div className="shoutout-mode" role="radiogroup" aria-labelledby="shoutout-mode-label">
      {MODE_CHOICES.map(choice => (
        <button
          key={choice.mode}
          type="button"
          role="radio"
          aria-checked={mode === choice.mode}
          className={mode === choice.mode ? 'shoutout-mode-option is-active' : 'shoutout-mode-option'}
          disabled={disabled}
          onClick={() => mode !== choice.mode && onChange(choice.mode)}
        >
          {choice.label}
        </button>
      ))}
    </div>
    <div className="control-rule-help">
      {`Patrons pay to put a short message on the TV: it pops up big for a few seconds, then scrolls in the ticker for ${minutes} minutes.`}{' '}
      Every message is checked for bad words and links first. Auto-play shows it as soon as it is paid for; Review first waits for you or an admin in the
      Shout-outs tab, and a rejected one is refunded in full.
    </div>
  </div>
);

interface ShoutoutReviewProps {
  mode: ShoutoutMode;
  minutes: number;
  pending: ReviewShoutout[];
  live?: ReviewShoutout[];
  recent: ReviewShoutout[];
  bans?: ShoutoutBan[];
  busyId: number;
  onDecide(shoutout: ReviewShoutout, action: ShoutoutAction): void;
  onUnban?(ban: ShoutoutBan): void;
  /** Asks before a ban. Swapped out in tests. */
  confirm?(text: string): boolean;
}

const STATUS_LABEL: { [status: string]: string } = {
  approved: 'On the TV',
  shown: 'Shown',
  rejected: 'Refunded',
  removed: 'Deleted',
};

const askBrowser = (text: string) => window.confirm(text);

interface BanButtonProps {
  shoutout: ReviewShoutout;
  busyId: number;
  confirm(text: string): boolean;
  onDecide(shoutout: ReviewShoutout, action: ShoutoutAction): void;
}

/** "Ban from this ticker", asked about first. Missing when the account is gone. */
const BanButton = ({ shoutout, busyId, confirm, onDecide }: BanButtonProps) =>
  !shoutout.userId ? null : (
    <button
      type="button"
      className="shoutout-review-ban"
      disabled={busyId !== null}
      onClick={() => {
        const who = shoutout.username || 'this person';
        if (
          confirm(
            `Ban ${who} from this TV's ticker? They won't be able to buy shout-outs here. Their shout-outs on the TV come off now, and any waiting are refunded.`,
          )
        ) {
          onDecide(shoutout, 'ban');
        }
      }}
    >
      Ban from this ticker
    </button>
  );

const ShoutoutReview = ({
  mode,
  minutes,
  pending,
  live = [],
  recent,
  bans = [],
  busyId,
  onDecide,
  onUnban = () => null,
  confirm = askBrowser,
}: ShoutoutReviewProps) => (
  <div className="shoutout-review">
    <p className="control-hint">
      {mode === 'review'
        ? `Approve a shout-out to pop it up on the TV, then show it in the ticker for ${minutes} minutes. Rejecting refunds the patron in full.`
        : mode === 'auto'
        ? 'Shout-outs go on the TV as soon as they are paid for.'
        : 'Shout-outs are off. Any still waiting can be approved or rejected here.'}
    </p>

    {live.length > 0 && (
      <>
        <div className="control-rule-label shoutout-review-heading">On the TV now</div>
        <p className="control-hint">Delete takes it off the TV straight away. It is not refunded.</p>
        <ul className="shoutout-review-list">
          {live.map(shoutout => (
            <li key={shoutout.id} className="shoutout-review-row is-live">
              <div className="shoutout-review-text">
                <span className="shoutout-review-name">{shoutout.username || 'Someone'}:</span> {shoutout.message}
              </div>
              <div className="shoutout-review-meta">
                {shoutout.secondsLeft ? `${Math.ceil(shoutout.secondsLeft / 60)} min left` : 'On the TV'}
              </div>
              <div className="shoutout-review-actions">
                <button
                  type="button"
                  className="shoutout-review-delete"
                  disabled={busyId !== null}
                  onClick={() => onDecide(shoutout, 'remove')}
                >
                  {busyId === shoutout.id ? 'Saving' : 'Delete'}
                </button>
                <BanButton shoutout={shoutout} busyId={busyId} confirm={confirm} onDecide={onDecide} />
              </div>
            </li>
          ))}
        </ul>
      </>
    )}

    {(live.length > 0 || pending.length > 0) && <div className="control-rule-label shoutout-review-heading">Waiting for review</div>}

    {pending.length === 0 && <p className="jukebox-empty">Nothing waiting for review.</p>}

    <ul className="shoutout-review-list">
      {pending.map(shoutout => (
        <li key={shoutout.id} className="shoutout-review-row">
          <div className="shoutout-review-text">
            <span className="shoutout-review-name">{shoutout.username || 'Someone'}:</span> {shoutout.message}
          </div>
          <div className="shoutout-review-meta">Paid {formatPrice(shoutout.amountCents, 'usd')}</div>
          <div className="shoutout-review-actions">
            <button
              type="button"
              className="shoutout-review-approve"
              disabled={busyId !== null}
              onClick={() => onDecide(shoutout, 'approve')}
            >
              {busyId === shoutout.id ? 'Saving' : 'Approve'}
            </button>
            <button
              type="button"
              className="shoutout-review-reject"
              disabled={busyId !== null}
              onClick={() => onDecide(shoutout, 'reject')}
            >
              Reject and refund
            </button>
          </div>
          <div className="shoutout-review-actions shoutout-review-actions-more">
            <BanButton shoutout={shoutout} busyId={busyId} confirm={confirm} onDecide={onDecide} />
          </div>
        </li>
      ))}
    </ul>

    {recent.length > 0 && (
      <>
        <div className="control-rule-label shoutout-review-heading">Recent</div>
        <ul className="shoutout-review-list">
          {recent.map(shoutout => (
            <li key={shoutout.id} className={`shoutout-review-row is-${shoutout.status}`}>
              <div className="shoutout-review-text">
                <span className="shoutout-review-name">{shoutout.username || 'Someone'}:</span> {shoutout.message}
              </div>
              <div className="shoutout-review-meta">
                {STATUS_LABEL[shoutout.status] || shoutout.status}
                {shoutout.status === 'approved' && shoutout.secondsLeft
                  ? ` · ${Math.ceil(shoutout.secondsLeft / 60)} min left`
                  : ''}
              </div>
            </li>
          ))}
        </ul>
      </>
    )}

    {bans.length > 0 && (
      <>
        <div className="control-rule-label shoutout-review-heading">Banned from ticker</div>
        <ul className="shoutout-review-list">
          {bans.map(ban => (
            <li key={ban.userId} className="shoutout-review-row shoutout-ban-row">
              <div className="shoutout-ban-main">
                <div className="shoutout-review-text">
                  <span className="shoutout-review-name">{ban.username || 'Someone'}</span>
                </div>
                <div className="shoutout-review-meta">
                  Banned {new Date(ban.bannedAt).toLocaleDateString()}
                  {ban.bannedBy ? ` by ${ban.bannedBy}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="shoutout-review-unban"
                disabled={busyId !== null}
                onClick={() => onUnban(ban)}
              >
                {busyId === -ban.userId ? 'Saving' : 'Unban'}
              </button>
            </li>
          ))}
        </ul>
      </>
    )}
  </div>
);

export default ShoutoutReview;
